/**
 * Runner Screen
 *
 * Responsibilities:
 *   - Render the current session item and progress ("N of M" + bar).
 *   - Yes/No buttons record an answer via session.recordAnswer, advance,
 *     and re-render.
 *   - Back control steps to the previous item via session.backOneItem
 *     (disabled at index 0).
 *   - Home control exits to Home, preserving the session.
 *   - On the final answer, call session.completeSession and navigate
 *     to Summary.
 *   - Debounce Yes/No/Back at 300ms to prevent accidental double-advance.
 *   - Announce "Item N of M: <item>" via the api.announce helper after
 *     each render for screen-reader users.
 *   - On any session-write failure (writeOk === false), surface
 *     api.showError("Couldn't save — progress may not survive close")
 *     per design requirement. The Runner never calls safeWrite directly;
 *     session.js remains the sole writer of STORAGE_KEYS.SESSION.
 *
 * Contract (matches Batch 2 stub signature):
 *   renderRunner(root, params, api)
 *     root:   the #app element
 *     params: unused (Runner reads session from storage)
 *     api:    { navigateTo, showToast, showError, announce }
 */

import {
  getSession,
  recordAnswer,
  backOneItem,
  completeSession
} from '../session.js';

const WRITE_FAIL_MSG = "Couldn't save — progress may not survive close";
const DEBOUNCE_MS = 300;

let locked = false;
function withDebounce(fn) {
  return (...args) => {
    if (locked) return;
    locked = true;
    try {
      fn(...args);
    } finally {
      setTimeout(() => { locked = false; }, DEBOUNCE_MS);
    }
  };
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderFromSession(root, session, api) {
  const total = session.items.length;
  const idx = session.currentIndex;
  const itemText = session.items[idx];
  const percent = total === 0 ? 0 : (idx / total) * 100;

  root.innerHTML = `
    <main class="screen screen-runner" aria-labelledby="runner-heading">
      <header class="runner-header">
        <button type="button" class="runner-back" data-action="back"
                aria-label="Previous item"${idx === 0 ? ' disabled' : ''}>
          ← Back
        </button>
        <h1 id="runner-heading" class="visually-hidden" tabindex="-1">
          ${escapeHtml(session.listName)}
        </h1>
        <button type="button" class="runner-home" data-action="home"
                aria-label="Exit to home">Home</button>
      </header>

      <div class="runner-progress" aria-hidden="true">
        <div class="runner-progress-label">${idx + 1} of ${total}</div>
        <div class="runner-progress-bar">
          <div class="runner-progress-bar__fill" style="width: ${percent}%"></div>
        </div>
      </div>

      <div class="runner-item-wrap">
        <p class="runner-item">${escapeHtml(itemText)}</p>
      </div>

      <div class="runner-actions">
        <button type="button" class="runner-btn runner-btn--no"
                data-action="no" aria-label="Mark as missing">
          <span class="runner-btn__icon" aria-hidden="true">\u{1F577}\u{FE0F}</span>
          <span class="runner-btn__label">No</span>
        </button>
        <button type="button" class="runner-btn runner-btn--yes"
                data-action="yes" aria-label="Mark as packed">
          <span class="runner-btn__icon" aria-hidden="true">\u{1F48B}</span>
          <span class="runner-btn__label">Yes</span>
        </button>
      </div>
    </main>
  `;

  api.announce(`Item ${idx + 1} of ${total}: ${itemText}`);

  const handleAnswer = withDebounce((answer) => {
    const { session: next, writeOk: answerOk } = recordAnswer(answer);
    if (next.currentIndex >= next.items.length) {
      const { writeOk: completeOk } = completeSession();
      if (!answerOk || !completeOk) {
        api.showError(WRITE_FAIL_MSG);
      }
      api.navigateTo('summary', {});
      return;
    }
    if (!answerOk) {
      api.showError(WRITE_FAIL_MSG);
    }
    renderFromSession(root, next, api);
  });

  const handleBack = withDebounce(() => {
    const { session: next, writeOk } = backOneItem();
    if (!writeOk) {
      api.showError(WRITE_FAIL_MSG);
    }
    renderFromSession(root, next, api);
  });

  const handleHome = () => {
    api.navigateTo('home', {});
  };

  root.querySelector('[data-action="yes"]').addEventListener('click', () => handleAnswer('yes'));
  root.querySelector('[data-action="no"]').addEventListener('click', () => handleAnswer('no'));
  const backBtn = root.querySelector('[data-action="back"]');
  if (!backBtn.disabled) {
    backBtn.addEventListener('click', handleBack);
  }
  root.querySelector('[data-action="home"]').addEventListener('click', handleHome);
}

export function renderRunner(root, _params, api) {
  const session = getSession();

  if (!session) {
    api.navigateTo('home', {});
    return;
  }

  if (session.completed) {
    api.navigateTo('summary', {});
    return;
  }

  renderFromSession(root, session, api);
}
